import { Link } from "@tanstack/react-router"

import type { CompanyRunResearch } from "@/admin/monitoring/company-run-research-data"
import type { FileEvidenceTarget } from "@/admin/monitoring/file-evidence-data"
import { StoredDetails } from "@/admin/monitoring/stored-value"
import { BandChip } from "@/components/terminal/band-mark"
import { detectionTextClassByClass } from "@/components/terminal/detection-text-classes"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AssessmentBand } from "@/constants/terminal/assessment-band"
import {
  dataByDetectionClass,
  DetectionClass,
} from "@/constants/terminal/detection-class"
import {
  dataByDetectionLifecycleState,
  type DetectionLifecycleState,
} from "@/constants/terminal/detection-lifecycle-state"
import { ScoreBreakdownKind } from "@/constants/terminal/score-breakdown-kind"
import {
  dataByUnclearReason,
  type UnclearReason,
} from "@/constants/terminal/unclear-reason"
import type { ScoreBreakdown } from "@/terminal/file/assessment-value-schemas"

// The console mutes Unclear, which is no verdict, so it never reads as one.
export const StoredBand = ({
  band,
  size = "sm",
}: {
  band: AssessmentBand
  size?: "sm" | "lg"
}) => (
  <BandChip
    band={band}
    size={size}
    style={
      band === AssessmentBand.UNCLEAR
        ? { color: "var(--terminal-text-secondary)" }
        : undefined
    }
  />
)

export const StoredAssessment = ({
  band,
  score,
}: {
  band: AssessmentBand | null
  score: number | null
}) => (
  <span className="inline-flex items-center gap-2">
    {band ? <StoredBand band={band} /> : "Pending"}
    {score !== null && <span>{score}</span>}
  </span>
)

export type OpenEvidence = (target: FileEvidenceTarget, title: string) => void

type BoardDetection = {
  key: string
  title: string
  summary: string
  class: DetectionClass
  lifecycleState: DetectionLifecycleState
  typeLabel: string
  details: unknown
  // A stored Detection opens its evidence; a rescored one has no row.
  detectionId: string | null
}

export const AssessmentHeadline = ({
  band,
  score,
  unclearReason,
}: {
  band: AssessmentBand | null
  score: number | null
  unclearReason: UnclearReason | null
}) => (
  <div className="space-y-2">
    <p className="flex items-center gap-4">
      {band ? (
        <StoredBand band={band} size="lg" />
      ) : (
        <span className="text-muted-foreground">Pending</span>
      )}
      {score !== null && (
        <span className="font-display text-5xl leading-none font-black tabular-nums">
          {score}
        </span>
      )}
    </p>
    {unclearReason && (
      <p className="text-muted-foreground">
        {dataByUnclearReason[unclearReason].label}
      </p>
    )}
  </div>
)

// A stored File's Score Input opens its supporting records; a rescored one
// has no stored row to open.
export const ScoreBreakdownView = ({
  breakdown,
  onOpenScore,
}: {
  breakdown: ScoreBreakdown
  onOpenScore?: (key: string, label: string) => void
}) => (
  <section className="space-y-3">
    <div className="flex flex-wrap items-baseline justify-between gap-x-4">
      <h2 className="text-lg font-semibold">Why this Assessment</h2>
      <Link
        className="text-terminal-accent text-xs hover:underline"
        to="/terminal/admin/policy-tuner"
      >
        Open in Policy Tuner
      </Link>
    </div>
    {breakdown.kind === ScoreBreakdownKind.UNCLEAR ? (
      <ul className="space-y-2">
        {breakdown.reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    ) : (
      <>
        <p>
          Baseline score{" "}
          <span className="tabular-nums">{breakdown.baselineScore}</span>, final
          score <span className="tabular-nums">{breakdown.finalScore}</span>.
        </p>
        <Table className="min-w-xl text-xs [&_td]:px-3 [&_td]:py-2.5 [&_th]:px-3 [&_th]:text-xs [&_th]:whitespace-nowrap">
          <TableHeader>
            <TableRow>
              <TableHead>Score Input</TableHead>
              {["Before", "Adjustment", "Floor", "Cap", "After"].map(
                (label) => (
                  <TableHead key={label} className="text-right">
                    {label}
                  </TableHead>
                )
              )}
            </TableRow>
          </TableHeader>
          <TableBody className="[&_td:not(:first-child)]:text-right [&_td:not(:first-child)]:tabular-nums">
            {breakdown.rows.map((row) => (
              <TableRow key={row.scoreInputKey}>
                <TableCell>
                  {onOpenScore ? (
                    <button
                      className="text-terminal-accent text-left hover:underline"
                      onClick={() => onOpenScore(row.scoreInputKey, row.label)}
                    >
                      {row.label}
                    </button>
                  ) : (
                    row.label
                  )}
                </TableCell>
                <TableCell>{row.scoreBefore}</TableCell>
                <TableCell>{row.pointAdjustment}</TableCell>
                <TableCell>{row.minimumScore ?? "None"}</TableCell>
                <TableCell>{row.maximumScore ?? "None"}</TableCell>
                <TableCell>{row.scoreAfter}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </>
    )}
  </section>
)

const detectionClassesBySeverity = [
  DetectionClass.FIRE,
  DetectionClass.SMOKE,
  DetectionClass.SPARK,
]

export const DetectionBoard = ({
  detections,
  note,
  onOpenEvidence,
}: {
  detections: BoardDetection[]
  note?: string
  onOpenEvidence: OpenEvidence
}) => (
  <section className="space-y-4">
    <div className="space-y-1">
      <h2 className="text-lg font-semibold">Detection Board</h2>
      {detections.length > 0 && (
        <p className="flex flex-wrap gap-x-4 text-xs tabular-nums">
          {detectionClassesBySeverity.map((detectionClass) => {
            const count = detections.filter(
              (detection) => detection.class === detectionClass
            ).length
            return (
              count > 0 && (
                <span
                  key={detectionClass}
                  className={detectionTextClassByClass[detectionClass]}
                >
                  {count} {dataByDetectionClass[detectionClass].label}
                  {count === 1 ? "" : "s"}
                </span>
              )
            )
          })}
        </p>
      )}
      {note && <p className="text-muted-foreground">{note}</p>}
    </div>
    {detections.length === 0 && (
      <p className="text-muted-foreground">No Detections.</p>
    )}
    <div className="grid border-t lg:grid-cols-2 lg:gap-x-10">
      {detections.map(({ detectionId, ...detection }) => (
        <article
          className="border-border-faint space-y-2 border-b py-4"
          key={detection.key}
        >
          {detectionId ? (
            <button
              className="text-terminal-accent text-left font-semibold hover:underline"
              onClick={() =>
                onOpenEvidence(
                  { kind: "detection", id: detectionId },
                  detection.title
                )
              }
            >
              {detection.title}
            </button>
          ) : (
            <h3 className="font-semibold">{detection.title}</h3>
          )}
          <p className="flex flex-wrap gap-x-4 text-xs">
            <span className={detectionTextClassByClass[detection.class]}>
              {dataByDetectionClass[detection.class].label}
            </span>
            <span className="text-muted-foreground">
              {dataByDetectionLifecycleState[detection.lifecycleState].label}
            </span>
            <span className="text-muted-foreground">{detection.typeLabel}</span>
          </p>
          <p className="max-w-prose">{detection.summary}</p>
          <StoredDetails
            summary="Stored Detection details"
            value={detection.details}
          />
        </article>
      ))}
    </div>
  </section>
)

// A Run's stored Assessment and its Detection Board as of it. A Detection
// this Run revised says so; one it retracted stays shown as Retracted.
export const FileAssessment = ({
  parts,
  onOpenEvidence,
}: {
  parts: CompanyRunResearch["parts"] & {
    assessment: NonNullable<CompanyRunResearch["parts"]["assessment"]>
  }
  onOpenEvidence: OpenEvidence
}) => (
  <div className="space-y-12">
    <AssessmentHeadline
      band={parts.assessment.band}
      score={parts.assessment.score}
      unclearReason={parts.assessment.unclearReason}
    />
    {parts.assessment.scoreBreakdown && (
      <ScoreBreakdownView
        breakdown={parts.assessment.scoreBreakdown}
        onOpenScore={(key, label) =>
          onOpenEvidence({ kind: "score", key }, label)
        }
      />
    )}
    <DetectionBoard
      detections={parts.revisions.map((detection) => ({
        key: detection.id,
        title: detection.presentation.title,
        summary: detection.presentation.summary,
        class: detection.class,
        lifecycleState: detection.lifecycleState,
        typeLabel: detection.isRevisedInRun
          ? `${detection.typeSnapshot.label}, revised in this Run`
          : detection.typeSnapshot.label,
        details: detection,
        detectionId: detection.id,
      }))}
      onOpenEvidence={onOpenEvidence}
    />
  </div>
)
